import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import { 
  FaUsers, 
  FaDrumstickBite, 
  FaChartBar, 
  FaCog,
  FaSignOutAlt,
  FaPlus,
  FaEdit,
  FaTrash,
  FaCoins,
  FaChevronDown,
  FaTimes,
  FaDownload,
  FaFileExcel,
  FaMoneyBillWave
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/api';

const AdminContainer = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: var(--light-gray);
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
`;

const AdminHeader = styled.div`
  background: var(--white-color);
  padding: 30px;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  margin-bottom: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
`;

const AdminTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--secondary-color);
`;

const AdminActions = styled.div`
  display: flex;
  gap: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const ActionButton = styled(motion.button)`
  background: var(--primary-color);
  color: var(--white-color);
  padding: 12px 20px;
  border-radius: var(--border-radius);
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: var(--transition);
  
  &:hover {
    background: #e67e00;
  }
  
  &.secondary {
    background: var(--secondary-color);
    
    &:hover {
      background: #444;
    }
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
`;

const StatCard = styled(motion.div)`
  background: var(--white-color);
  padding: 30px;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  text-align: center;
  transition: var(--transition);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  }
`;

const StatIcon = styled.div`
  width: 60px;
  height: 60px;
  background: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  font-size: 24px;
  color: var(--white-color);
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 10px;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: var(--secondary-color);
  font-weight: 500;
`;

const ContentSection = styled.div`
  background: var(--white-color);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  overflow: visible;
`;

const SectionHeader = styled.div`
  padding: 20px 30px;
  border-bottom: 1px solid var(--light-gray);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--secondary-color);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  padding: 15px;
  text-align: left;
  background: var(--light-gray);
  font-weight: 600;
  color: var(--secondary-color);
  border-bottom: 1px solid #ddd;
`;

const TableCell = styled.td`
  padding: 15px;
  border-bottom: 1px solid var(--light-gray);
  color: var(--secondary-color);
`;

const TableRow = styled.tr`
  transition: var(--transition);
  
  &:hover {
    background: var(--light-gray);
  }
`;

const StatusBadge = styled.span`
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  
  &.active {
    background: #d4edda;
    color: #155724;
  }
  
  &.pending {
    background: #fff3cd;
    color: #856404;
  }
  
  &.inactive {
    background: #f8d7da;
    color: #721c24;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: var(--transition);
  
  &.edit {
    background: #007bff;
    color: white;
    
    &:hover {
      background: #0056b3;
    }
  }
  
  &.delete {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
  }
`;

// Modal Stilleri
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContent = styled(motion.div)`
  background: var(--white-color);
  border-radius: var(--border-radius);
  padding: 30px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--secondary-color);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: var(--secondary-color);
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    color: var(--primary-color);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--secondary-color);
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid var(--light-gray);
  border-radius: var(--border-radius);
  font-size: 16px;
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 12px;
  border: 2px solid var(--light-gray);
  border-radius: var(--border-radius);
  font-size: 16px;
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
`;

const ModalButton = styled(motion.button)`
  padding: 12px 24px;
  border-radius: var(--border-radius);
  font-size: 16px;
  font-weight: 600;
  transition: var(--transition);
  
  &.primary {
    background: var(--primary-color);
    color: var(--white-color);
    
    &:hover {
      background: #e67e00;
    }
  }
  
  &.secondary {
    background: var(--light-gray);
    color: var(--secondary-color);
    
    &:hover {
      background: #ddd;
    }
  }
`;

// Dropdown Stilleri
const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled(motion.button)`
  background: var(--white-color);
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
  padding: 12px 20px;
  border-radius: var(--border-radius);
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: var(--transition);
  cursor: pointer;
  
  &:hover {
    background: var(--primary-color);
    color: var(--white-color);
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--white-color);
  border: 2px solid var(--primary-color);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  z-index: 100;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 20px;
  background: none;
  border: none;
  text-align: left;
  font-size: 14px;
  color: var(--secondary-color);
  transition: var(--transition);
  cursor: pointer;
  
  &:hover {
    background: var(--light-gray);
    color: var(--primary-color);
  }
`;

// Filtre Stilleri
const FilterSection = styled.div`
  background: var(--white-color);
  padding: 20px 30px;
  border-bottom: 1px solid var(--light-gray);
  display: flex;
  gap: 20px;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const FilterSelect = styled.select`
  padding: 10px 15px;
  border: 2px solid var(--light-gray);
  border-radius: var(--border-radius);
  font-size: 14px;
  background: var(--white-color);
  color: var(--secondary-color);
  cursor: pointer;
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const ExcelButton = styled(motion.button)`
  background: var(--white-color);
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
  padding: 10px 20px;
  border-radius: var(--border-radius);
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: var(--transition);
  cursor: pointer;
  
  &:hover {
    background: var(--primary-color);
    color: var(--white-color);
  }
`;

// Satış Raporu Modal Stilleri
const SalesReportModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999 !important;
`;

const SalesReportContent = styled(motion.div)`
  background: var(--white-color);
  border-radius: var(--border-radius);
  padding: 30px;
  max-width: 1000px;
  width: 95%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;

const SalesReportHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--light-gray);
`;

const SalesReportTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: var(--secondary-color);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SalesReportActions = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
`;

const ExportButton = styled(motion.button)`
  background: #28a745;
  color: var(--white-color);
  border: none;
  padding: 12px 20px;
  border-radius: var(--border-radius);
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: var(--transition);
  cursor: pointer;
  
  &:hover {
    background: #218838;
  }
`;

const SalesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
`;

const SalesTableHeader = styled.th`
  padding: 15px 10px;
  text-align: left;
  background: var(--primary-color);
  color: var(--white-color);
  font-weight: 600;
  border: 1px solid #ddd;
`;

const SalesTableCell = styled.td`
  padding: 12px 10px;
  border: 1px solid #ddd;
  color: var(--secondary-color);
`;

const SalesTableRow = styled.tr`
  transition: var(--transition);
  
  &:nth-child(even) {
    background: #f8f9fa;
  }
  
  &:hover {
    background: var(--light-gray);
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--secondary-color);
  font-size: 1.1rem;
  background: var(--light-gray);
  border-radius: var(--border-radius);
  margin-top: 20px;
`;

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSalesReportModalOpen, setIsSalesReportModalOpen] = useState(false);
  const [isTahsilatlarModalOpen, setIsTahsilatlarModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    hisseSahibi: '',
    hisseAdedi: '',
    aracı: '',
    kurum: '',
    fiyat: ''
  });
  const [selectedType, setSelectedType] = useState('Tüm Türler');
  const [selectedBreed, setSelectedBreed] = useState('Tüm Cinsler');
  const [totalAnimals, setTotalAnimals] = useState(0);
  const [tahsilatlarData, setTahsilatlarData] = useState([]);
  const [tahsilatlarLoading, setTahsilatlarLoading] = useState(false);
  
  // Kullanıcı bilgileri (gerçek uygulamada localStorage'dan veya context'ten gelecek)
  const [currentUser] = useState({
    id: 1,
    name: 'Ahmet Yılmaz',
    role: 'Yönetici', // 'Personel', 'Kurum Mesulü', 'Yönetici' - Test için Yönetici olarak ayarlandı
    institution: 'SULTANLAR BESİCİLİK'
  });
  
  // Satış verileri (gerçek uygulamada API'den gelecek)
  const [salesData] = useState([
    {
      id: 1,
      hisseSahibi: 'Hakkı Tanrıveri',
      hisseAdedi: 5,
      aracı: 'Mehmet Kaya',
      kurum: 'SULTANLAR BESİCİLİK',
      fiyat: 25000,
      tarih: '2024-01-15',
      personelId: 1,
      personelAdi: 'Ahmet Yılmaz'
    },
    {
      id: 2,
      hisseSahibi: 'Fatma Demir',
      hisseAdedi: 3,
      aracı: 'Ayşe Özkan',
      kurum: 'SULTANLAR BESİCİLİK',
      fiyat: 15000,
      tarih: '2024-01-14',
      personelId: 2,
      personelAdi: 'Mehmet Kaya'
    },
    {
      id: 3,
      hisseSahibi: 'Ali Veli',
      hisseAdedi: 2,
      aracı: 'Zeynep Yılmaz',
      kurum: 'BAŞKA KURUM',
      fiyat: 10000,
      tarih: '2024-01-13',
      personelId: 3,
      personelAdi: 'Zeynep Yılmaz'
    }
  ]);

  const stats = [
    { icon: <FaDrumstickBite />, number: totalAnimals, label: 'Toplam Hayvan' },
    { icon: <FaChartBar />, number: '₺45.000', label: 'Aylık Gelir' },
    { icon: <FaCog />, number: 23, label: 'Aktif Sipariş' }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Hisse Satışı Formu:', formData);
    alert('Hisse satışı başarıyla eklendi!');
    setIsModalOpen(false);
    setFormData({
      hisseSahibi: '',
      hisseAdedi: '',
      aracı: '',
      kurum: '',
      fiyat: ''
    });
  };

  const handleDropdownItemClick = (action) => {
    console.log('Dropdown item clicked:', action);
    setIsDropdownOpen(false);
    
    if (action === 'report') {
      setIsSalesReportModalOpen(true);
    }
  };

  // Kullanıcı rolüne göre satış verilerini filtrele
  const getFilteredSalesData = () => {
    switch (currentUser.role) {
      case 'Personel':
        // Sadece kendi satışları
        return salesData.filter(sale => sale.personelId === currentUser.id);
      case 'Kurum Mesulü':
        // Sadece kendi kurumuna ait satışlar
        return salesData.filter(sale => sale.kurum === currentUser.institution);
      case 'Yönetici':
        // Tüm satışlar
        return salesData;
      default:
        return [];
    }
  };

  // Tahsilatlar verilerini çek
  const fetchTahsilatlarData = async () => {
    setTahsilatlarLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.TAHSILATLAR(`userId=${encodeURIComponent(currentUser.id)}&userRole=${encodeURIComponent(currentUser.role)}&userInstitution=${encodeURIComponent(currentUser.institution || '')}`));
      if (response.ok) {
        const data = await response.json();
        setTahsilatlarData(data);
        console.log('📊 Tahsilatlar verisi çekildi:', data);
      } else {
        console.error('Tahsilatlar verisi çekilemedi:', response.statusText);
        setTahsilatlarData([]);
      }
    } catch (error) {
      console.error('Tahsilatlar verisi çekme hatası:', error);
      setTahsilatlarData([]);
    } finally {
      setTahsilatlarLoading(false);
    }
  };

  // Excel dosyası indirme fonksiyonu
  const exportToExcel = () => {
    const filteredData = getFilteredSalesData();
    
    // Excel için veri hazırla
    const excelData = [
      ['ID', 'Hisse Sahibi', 'Hisse Adedi', 'Aracı', 'Kurum', 'Fiyat (TL)', 'Tarih', 'Personel'],
      ...filteredData.map(sale => [
        sale.id,
        sale.hisseSahibi,
        sale.hisseAdedi,
        sale.aracı,
        sale.kurum,
        sale.fiyat,
        sale.tarih,
        sale.personelAdi
      ])
    ];

    // Workbook oluştur
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    
    // Sütun genişliklerini ayarla
    const colWidths = [
      { wch: 5 },   // ID
      { wch: 20 },  // Hisse Sahibi
      { wch: 12 },  // Hisse Adedi
      { wch: 15 },  // Aracı
      { wch: 20 },  // Kurum
      { wch: 15 },  // Fiyat
      { wch: 12 },  // Tarih
      { wch: 20 }   // Personel
    ];
    ws['!cols'] = colWidths;
    
    // Sheet'i workbook'a ekle
    XLSX.utils.book_append_sheet(wb, ws, 'Satış Raporu');
    
    // Dosyayı indir
    const fileName = `satis_raporu_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    
    alert('Excel dosyası başarıyla indirildi!');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.dropdown-container')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    console.log('Modal state değişti:', isModalOpen);
  }, [isModalOpen]);

  // Toplam hayvan sayısını çek
  useEffect(() => {
    const fetchTotalAnimals = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.ANIMALS_COUNT);
        if (response.ok) {
          const data = await response.json();
          setTotalAnimals(data.total);
          console.log('📊 Toplam hayvan sayısı:', data.total);
        } else {
          console.error('Hayvan sayısı çekilemedi:', response.statusText);
        }
      } catch (error) {
        console.error('Hayvan sayısı çekme hatası:', error);
      }
    };

    fetchTotalAnimals();
  }, []);

  // Sayfa yüklendiğinde test
  useEffect(() => {
    console.log('=== ADMINPAGE YÜKLENDİ ===');
    console.log('isModalOpen başlangıç değeri:', isModalOpen);
    console.log('AdminPage componenti çalışıyor!');
  }, []);


  const customers = [
    { id: 1, name: 'Ahmet Yılmaz', email: 'ahmet@email.com', phone: '0555 123 4567', status: 'active' },
    { id: 2, name: 'Fatma Demir', email: 'fatma@email.com', phone: '0555 234 5678', status: 'active' },
    { id: 3, name: 'Mehmet Kaya', email: 'mehmet@email.com', phone: '0555 345 6789', status: 'pending' },
    { id: 4, name: 'Ayşe Özkan', email: 'ayse@email.com', phone: '0555 456 7890', status: 'inactive' }
  ];

  const animals = [
    { id: 1, type: 'Holstein Dana', age: '2 yaş', weight: '450 kg', price: '45.000 TL', status: 'active' },
    { id: 2, type: 'Angus Düve', age: '1.5 yaş', weight: '380 kg', price: '38.000 TL', status: 'active' },
    { id: 3, type: 'Merinos Koyun', age: '3 yaş', weight: '65 kg', price: '8.500 TL', status: 'pending' },
    { id: 4, type: 'Kuzu', age: '6 ay', weight: '25 kg', price: '3.500 TL', status: 'active' }
  ];

  return (
    <AdminContainer>
      <Container>
        <AdminHeader>
          <AdminTitle>SULTANLAR BESİCİLİK - Personel Yönetim Sistemi</AdminTitle>
          <AdminActions>
            <DropdownContainer className="dropdown-container">
              <DropdownButton
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDropdownOpen(!isDropdownOpen);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaCoins />
                Hisse Satışı
                <FaChevronDown />
              </DropdownButton>
              <AnimatePresence>
                {isDropdownOpen && (
                  <DropdownMenu
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                  <DropdownItem onClick={(e) => {
                    e.stopPropagation();
                    handleDropdownItemClick('list');
                  }}>
                    Hisse Listesi
                  </DropdownItem>
                  <DropdownItem onClick={(e) => {
                    e.stopPropagation();
                    handleDropdownItemClick('report');
                  }}>
                    Satış Raporu
                  </DropdownItem>
                  <DropdownItem onClick={(e) => {
                    e.stopPropagation();
                    handleDropdownItemClick('history');
                  }}>
                    Satış Geçmişi
                  </DropdownItem>
                  </DropdownMenu>
                )}
              </AnimatePresence>
            </DropdownContainer>
            <ActionButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaCog />
              Ayarlar
            </ActionButton>
            <ActionButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaChartBar />
              Raporlar
            </ActionButton>
            <ActionButton
              onClick={() => {
                console.log('Tahsilatlar butonu tıklandı!');
                alert('Tahsilatlar butonu çalışıyor!');
                setIsTahsilatlarModalOpen(true);
                fetchTahsilatlarData();
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                background: '#28a745',
                color: 'white',
                border: '2px solid #28a745',
                fontWeight: 'bold',
                fontSize: '16px',
                padding: '15px 25px',
                boxShadow: '0 4px 8px rgba(40, 167, 69, 0.3)'
              }}
            >
              <FaMoneyBillWave style={{ marginRight: '8px' }} />
              💰 Tahsilatlar
            </ActionButton>
            <ActionButton
              className="secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaSignOutAlt />
              Çıkış
            </ActionButton>
          </AdminActions>
        </AdminHeader>

        <DashboardGrid>
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <StatIcon>{stat.icon}</StatIcon>
              <StatNumber>{stat.number}</StatNumber>
              <StatLabel>{stat.label}</StatLabel>
            </StatCard>
          ))}
        </DashboardGrid>

        <ContentSection>
          <SectionHeader>
            <SectionTitle>Hisse Satışları</SectionTitle>
            <div style={{ position: 'relative', zIndex: 10000 }}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('=== YENİ HİSSE SATIŞI BUTONU TIKLANDI ===');
                  console.log('Buton çalışıyor!');
                  alert('Buton tıklandı!');
                  setIsModalOpen(true);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  console.log('Mouse down event');
                }}
                onMouseUp={(e) => {
                  e.preventDefault();
                  console.log('Mouse up event');
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  console.log('Touch start event');
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  console.log('Touch end event');
                }}
                style={{
                  background: '#ff6b35',
                  color: 'white',
                  padding: '12px 20px',
                  border: '2px solid #ff6b35',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  zIndex: 9999,
                  position: 'relative',
                  pointerEvents: 'auto',
                  minWidth: '150px',
                  minHeight: '40px',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none'
                }}
              >
                <FaPlus />
                Yeni Hisse Satışı
              </button>
            </div>
          </SectionHeader>
          
          <FilterSection>
            <FilterSelect 
              value={selectedType} 
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="Tüm Türler">Tüm Türler</option>
              <option value="Büyükbaş">Büyükbaş</option>
              <option value="Küçükbaş">Küçükbaş</option>
            </FilterSelect>
            
            <FilterSelect 
              value={selectedBreed} 
              onChange={(e) => setSelectedBreed(e.target.value)}
            >
              <option value="Tüm Cinsler">Tüm Cinsler</option>
              <option value="Holstein">Holstein</option>
              <option value="Angus">Angus</option>
              <option value="Merinos">Merinos</option>
              <option value="Kıvırcık">Kıvırcık</option>
            </FilterSelect>
            
            <ExcelButton
              onClick={() => alert('Excel dosyası indiriliyor...')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaDownload />
              Excel İndir
            </ExcelButton>
          </FilterSection>
          
          <Table>
            <thead>
              <tr>
                <TableHeader>Sıra</TableHeader>
                <TableHeader>Hisse Sahibi</TableHeader>
                <TableHeader>Hisse Adedi</TableHeader>
                <TableHeader>Aracı Olan</TableHeader>
                <TableHeader>Kurum</TableHeader>
                <TableHeader>İşlemler</TableHeader>
              </tr>
            </thead>
            <tbody>
              <TableRow>
                <TableCell>1</TableCell>
                <TableCell>hakkı tanrıveri</TableCell>
                <TableCell>1</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>
                  <ActionButtons>
                    <IconButton className="edit">
                      <FaEdit />
                    </IconButton>
                    <IconButton className="delete">
                      <FaTrash />
                    </IconButton>
                  </ActionButtons>
                </TableCell>
              </TableRow>
            </tbody>
          </Table>
        </ContentSection>

        <ContentSection style={{ marginTop: '30px' }}>
          <SectionHeader>
            <SectionTitle>Hayvanlar</SectionTitle>
            <ActionButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPlus />
              Yeni Hayvan
            </ActionButton>
          </SectionHeader>
          
          <Table>
            <thead>
              <tr>
                <TableHeader>ID</TableHeader>
                <TableHeader>Tür</TableHeader>
                <TableHeader>Yaş</TableHeader>
                <TableHeader>Ağırlık</TableHeader>
                <TableHeader>Fiyat</TableHeader>
                <TableHeader>Durum</TableHeader>
                <TableHeader>İşlemler</TableHeader>
              </tr>
            </thead>
            <tbody>
              {animals.map((animal) => (
                <TableRow key={animal.id}>
                  <TableCell>{animal.id}</TableCell>
                  <TableCell>{animal.type}</TableCell>
                  <TableCell>{animal.age}</TableCell>
                  <TableCell>{animal.weight}</TableCell>
                  <TableCell>{animal.price}</TableCell>
                  <TableCell>
                    <StatusBadge className={animal.status}>
                      {animal.status === 'active' ? 'Aktif' : 
                       animal.status === 'pending' ? 'Beklemede' : 'Pasif'}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    <ActionButtons>
                      <IconButton className="edit">
                        <FaEdit />
                      </IconButton>
                      <IconButton className="delete">
                        <FaTrash />
                      </IconButton>
                    </ActionButtons>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </ContentSection>

        {/* Hisse Satışı Modal */}
        {isModalOpen && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }} onClick={() => setIsModalOpen(false)}>
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '10px',
              maxWidth: '500px',
              width: '90%',
              position: 'relative'
            }} onClick={(e) => e.stopPropagation()}>
              <h2>Yeni Hisse Satışı</h2>
              <p>Modal çalışıyor!</p>
              <button onClick={() => setIsModalOpen(false)}>Kapat</button>
            </div>
          </div>
        )}

        {/* Satış Raporu Modal */}
        {isSalesReportModalOpen && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999
          }} onClick={() => setIsSalesReportModalOpen(false)}>
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '10px',
              maxWidth: '1000px',
              width: '95%',
              maxHeight: '80vh',
              overflowY: 'auto',
              position: 'relative'
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #eee' }}>
                <h2 style={{ fontSize: '1.8rem', fontWeight: '600', color: '#333', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  📊 Satış Raporu
                </h2>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                  {currentUser.role === 'Yönetici' && (
                    <button
                      onClick={exportToExcel}
                      style={{
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      📄 Excel İndir
                    </button>
                  )}
                  <button 
                    onClick={() => setIsSalesReportModalOpen(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      color: '#333',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                <strong>Kullanıcı:</strong> {currentUser.name} ({currentUser.role})<br/>
                <strong>Kurum:</strong> {currentUser.institution}<br/>
                <strong>Gösterilen Veri:</strong> {
                  currentUser.role === 'Personel' ? 'Sadece kendi satışlarınız' :
                  currentUser.role === 'Kurum Mesulü' ? 'Kurumunuza ait tüm satışlar' :
                  'Tüm satışlar'
                }
              </div>

              {getFilteredSalesData().length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>ID</th>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Hisse Sahibi</th>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Hisse Adedi</th>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Aracı</th>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Kurum</th>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Fiyat (TL)</th>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Tarih</th>
                      <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Personel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredSalesData().map((sale) => (
                      <tr key={sale.id} style={{ transition: 'all 0.3s ease' }}>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.id}</td>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.hisseSahibi}</td>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.hisseAdedi}</td>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.aracı}</td>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.kurum}</td>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.fiyat.toLocaleString('tr-TR')}</td>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.tarih}</td>
                        <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{sale.personelAdi}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#333', fontSize: '1.1rem', background: '#f8f9fa', borderRadius: '8px', marginTop: '20px' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '15px', opacity: 0.5 }}>📊</div>
                  <div>Gösterilecek satış verisi bulunamadı.</div>
                  <div style={{ fontSize: '0.9rem', marginTop: '10px', opacity: 0.7 }}>
                    {currentUser.role === 'Personel' ? 'Henüz hiç satış yapmamışsınız.' :
                     currentUser.role === 'Kurum Mesulü' ? 'Kurumunuza ait satış bulunmuyor.' :
                     'Sistemde hiç satış kaydı bulunmuyor.'}
                  </div>
                </div>
              )}

              <div style={{ marginTop: '20px', padding: '15px', background: '#e8f5e8', borderRadius: '8px', fontSize: '0.9rem' }}>
                <strong>Toplam Satış:</strong> {getFilteredSalesData().length} adet<br/>
                <strong>Toplam Tutar:</strong> {getFilteredSalesData().reduce((sum, sale) => sum + sale.fiyat, 0).toLocaleString('tr-TR')} TL
              </div>
            </div>
          </div>
        )}

        {/* Tahsilatlar Modal */}
        {isTahsilatlarModalOpen && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999
          }} onClick={() => setIsTahsilatlarModalOpen(false)}>
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '10px',
              maxWidth: '1200px',
              width: '95%',
              maxHeight: '80vh',
              overflowY: 'auto',
              position: 'relative'
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #eee' }}>
                <h2 style={{ fontSize: '1.8rem', fontWeight: '600', color: '#333', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  💰 Tahsilatlar
                </h2>
                <button 
                  onClick={() => setIsTahsilatlarModalOpen(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    color: '#333',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                <strong>Kullanıcı:</strong> {currentUser.name} ({currentUser.role})<br/>
                <strong>Kurum:</strong> {currentUser.institution}<br/>
                <strong>Gösterilen Veri:</strong> {
                  currentUser.role === 'Personel' ? 'Sadece kendi aracı olduğunuz hisse sahipleri' :
                  currentUser.role === 'Kurum Mesulü' ? 'Kurumunuza ait tüm hisse sahipleri' :
                  'Tüm hisse sahipleri'
                }
              </div>

              {tahsilatlarLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#333', fontSize: '1.1rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '15px' }}>⏳</div>
                  <div>Tahsilat verileri yükleniyor...</div>
                </div>
              ) : tahsilatlarData.length > 0 ? (
                <>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>ID</th>
                        <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Hisse Sahibi</th>
                        <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Toplam Bakiye (TL)</th>
                        <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Ödeme Miktarı (TL)</th>
                        <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Kalan Bakiye (TL)</th>
                        <th style={{ padding: '15px 10px', textAlign: 'left', background: '#ff6b35', color: 'white', fontWeight: '600', border: '1px solid #ddd' }}>Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tahsilatlarData.map((tahsilat) => (
                        <tr key={tahsilat.id} style={{ transition: 'all 0.3s ease' }}>
                          <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{tahsilat.id}</td>
                          <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{tahsilat.hisse_sahibi}</td>
                          <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{tahsilat.toplam_bakiye.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{tahsilat.odeme_miktari.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>{tahsilat.kalan_bakiye.toLocaleString('tr-TR')}</td>
                          <td style={{ padding: '12px 10px', border: '1px solid #ddd', color: '#333' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              background: tahsilat.kalan_bakiye === 0 ? '#d4edda' : tahsilat.kalan_bakiye < tahsilat.toplam_bakiye * 0.5 ? '#fff3cd' : '#f8d7da',
                              color: tahsilat.kalan_bakiye === 0 ? '#155724' : tahsilat.kalan_bakiye < tahsilat.toplam_bakiye * 0.5 ? '#856404' : '#721c24'
                            }}>
                              {tahsilat.kalan_bakiye === 0 ? 'Tamamlandı' : tahsilat.kalan_bakiye < tahsilat.toplam_bakiye * 0.5 ? 'Kısmi Ödeme' : 'Beklemede'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ marginTop: '20px', padding: '15px', background: '#e8f5e8', borderRadius: '8px', fontSize: '0.9rem' }}>
                    <strong>Toplam Hisse Sahibi:</strong> {tahsilatlarData.length} kişi<br/>
                    <strong>Toplam Bakiye:</strong> {tahsilatlarData.reduce((sum, t) => sum + t.toplam_bakiye, 0).toLocaleString('tr-TR')} TL<br/>
                    <strong>Toplam Ödeme:</strong> {tahsilatlarData.reduce((sum, t) => sum + t.odeme_miktari, 0).toLocaleString('tr-TR')} TL<br/>
                    <strong>Kalan Bakiye:</strong> {tahsilatlarData.reduce((sum, t) => sum + t.kalan_bakiye, 0).toLocaleString('tr-TR')} TL
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#333', fontSize: '1.1rem', background: '#f8f9fa', borderRadius: '8px', marginTop: '20px' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '15px', opacity: 0.5 }}>💰</div>
                  <div>Gösterilecek tahsilat verisi bulunamadı.</div>
                  <div style={{ fontSize: '0.9rem', marginTop: '10px', opacity: 0.7 }}>
                    {currentUser.role === 'Personel' ? 'Henüz hiç hisse sahibi bulunmuyor.' :
                     currentUser.role === 'Kurum Mesulü' ? 'Kurumunuza ait hisse sahibi bulunmuyor.' :
                     'Sistemde hiç hisse sahibi kaydı bulunmuyor.'}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Container>
    </AdminContainer>
  );
};

export default AdminPage; 